import SearchIcon from '@mui/icons-material/Search';
import { FormLabel, Pagination, Stack } from '@mui/material';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { IDataTableSliceState } from '../../../types/IComponentTypes';
import type { IDataTableProps } from '../../../types/ITypes';
import { ECellType, EDataTableType, TableHeaders } from '../../data/Constants';
import { formatDateString } from '../../services/DataHelperFunctions';
import { getLocaleForGrid } from '../../services/Util';
import { AppDispatch, AppState } from '../../store/AppStore';
import { CustomToolbar } from './CustomToolbar';

/**
 * This method returns the column definitions to show the columns in the data table
 * @param {string} tableType
 * @param {AppDispatch} dispatch
 * @param t
 * @returns { GridColDef[]}
 */
function generateColumns(
  tableType: string,
  handleClickEdit: IDataTableProps['handleClickEdit'],
  dispatch: AppDispatch,
  t: TFunction
): GridColDef[] {
  return TableHeaders[tableType as keyof typeof TableHeaders].map((header) => {
    let valueGetter;
    let renderCell;
    switch (header.type) {
      case ECellType.Date:
        valueGetter = (value: string | Date | null) => {
          if (value) {
            return formatDateString(String(value));
          }
          return '';
        };
        break;
      case ECellType.Boolean:
        valueGetter = (value: boolean | null) => {
          return value ? t('labels.yes') : t('labels.no');
        };
        if (header.field === 'isActive') {
          renderCell = (params: GridRenderCellParams) => {
            const isActive = params.row.isActive;
            const text = isActive ? t('labels.yes') : t('labels.no');
            return <span>{text}</span>;
          };
        }
        break;
      case ECellType.Link:
        renderCell = (params: GridRenderCellParams) => (
          <FormLabel
            className="link justify-left p-0 "
            onClick={() => handleClickEdit?.(params.row.code, tableType, dispatch, t)}
            aria-label={`Edit ${header.field ? params.row[header.field] : ''}`}
          >
            {header.field ? params.row[header.field] : ''}
          </FormLabel>
        );

        break;
      default:
        break;
    }

    return {
      field: header.field ?? '',
      headerName: header.field === 'isActive' ? t('labels.isActive') : t(`labels.${header.field}`),
      width: header.width ?? 150,
      sortable: !!header.sort,
      filterable: !!header,
      valueGetter: valueGetter,
      renderCell: renderCell,
      cellClassName: (params: GridCellParams) => {
        if (header.field === 'isActive' && !params.row.isActive) {
          return 'cellWithValueAsNo';
        }
        return '';
      },
      renderHeader: () => {
        return (
          <span className="searchIconForColumn">
            {header.searchable ? <SearchIcon color="primary" /> : null}
            {header.field === 'isActive' ? t('labels.isActive') : t(`labels.${header.field}`)}
          </span>
        );
      },
    } as GridColDef;
  });
}

/**
 * This compoenent render the data table to show the rows and custom toolbar.
 * @param {IDataTableProps} props input props.
 * @returns {React.ReactElement} returns the Datatable component.
 */
export const DataTable = (props: IDataTableProps): React.ReactElement => {
  const {
    tableType,
    selectionModel,
    filterModel,
    fetchData,
    handleClickDelete,
    setSelectionModel,
    setFilterModel,
    handleClickNew,
    handleClickEdit,
    rowIdentifier = 'code',
    customFilterPanel,
    loading,
    setLoading,
  } = props;

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const localeForGrid = getLocaleForGrid(i18n.language);

  //state
  const [limit, setLimit] = useState(15);
  const [page, setPage] = useState(1);
  const [searchCode, setSearchCode] = useState('');

  /**
   * This method renders the records dropdown on the data table to select the limit to filter the data
   * @param {string} searchCode
   * @param {number} rowLength
   * @param t
   * @returns
   */

  function renderNoRecordsMessage(
    searchCode: string,
    rowLength: number,
    t: TFunction
  ): ReactElement {
    return (
      <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: searchCode ? 'red' : 'black' }}>
          {searchCode && rowLength === 0
            ? t('messages.noSearchRecords', { searchValue: searchCode })
            : t('messages.noDataFound', { name: t('labels.multiNumeric') })}
        </span>
      </Stack>
    );
  }

  // Select the appropriate state slice based on table type
  const isHistoryTable =
    tableType === EDataTableType.AllPropertiesHistory ||
    tableType === EDataTableType.PropertyHistory;

  const data: IDataTableSliceState = useSelector((state: AppState) =>
    isHistoryTable ? state.propertyHistory : state.multiNumeric
  ) as IDataTableSliceState;
  const rows = data.data[page];
  const totalPages = Math.ceil(data.totalCount / data.recordsPerPage) ?? 1;

  /**
   * This function triggers when change page
   * @param { React.ChangeEvent } _event event
   * @param { number } value input value
   */
  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number): void => {
    setPage(value);

    const requestPayload = data['requestPayload'] ?? {};

    //call the fetch api with new page value to get the rows
    Object.keys(requestPayload).length > 0
      ? fetchData({ ...requestPayload, page: value, limit: limit })
      : fetchData({ page: value, limit, searchCode });

    //reset filter  value on the data table
    setFilterModel({ items: [] });
  };

  /**
   * This function is used to check whether the delete button should disable or not
   * @returns {boolean} returns the disable delete button value
   */
  function checkIfDeleteButtonIsDisabled(): boolean {
    if (!selectionModel || selectionModel.ids.size === 0) {
      return true;
    }
    return !Array.from(selectionModel.ids).some(
      (id) => rows?.find((row: { code: string; isActive: boolean }) => row.code === id)?.isActive
    );
  }

  return (
    <>
      <DataGrid
        rows={rows}
        columns={generateColumns(tableType, handleClickEdit, dispatch, t)}
        checkboxSelection={!props.hideRowSelectionCheckboxes}
        onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => {
          setSelectionModel?.(newSelection);
        }}
        loading={loading}
        showToolbar
        slots={{
          toolbar: CustomToolbar as any,
          noRowsOverlay: () => {
            if (loading) {
              return null;
            }
            return renderNoRecordsMessage(searchCode, rows?.length, t);
          },
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'skeleton',
          },
          toolbar: {
            tableType,
            limit,
            page,
            loading,
            searchCode,
            setLimit,
            setPage,
            setSearchCode,
            fetchData,
            setFilterModel,
            setLoading,
            selectionModel,
            handleClickDelete,
            handleClickNew,
            customFilterPanel,
            checkIfDeleteButtonIsDisabled,
            requestPayload: data['requestPayload'] ?? {},
          } as any,
        }}
        rowSelectionModel={selectionModel}
        getRowId={(row) => {
          if (row[rowIdentifier]) return row[rowIdentifier];
          const index = rows?.indexOf(row);
          return index >= 0 ? `row-${index}` : `row-${Math.random()}`;
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        rowHeight={35}
        columnHeaderHeight={35}
        sx={{ marginRight: 1.5 }}
        filterModel={filterModel}
        onFilterModelChange={(params) => setFilterModel(params)}
        localeText={localeForGrid.components.MuiDataGrid.defaultProps.localeText}
      />
      {data.totalCount > 0 && (
        <Stack className="manage-tab-pagination">
          <Pagination
            size="small"
            color="primary"
            count={totalPages}
            page={page}
            onChange={handlePageChange}
          />
        </Stack>
      )}
    </>
  );
};
